/* eslint-disable react/prop-types */
import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

// --- HELPERS ---
const createQuantityAmount = () => ({
  quantity: "",
  amount: "",
});

const createItem = () => ({
  recordingDate: "",
  documentNumber: "",
  documentDate: "",
  description: "",
  correspondingAccount: "",
  bought: createQuantityAmount(),
  sold: createQuantityAmount(),
  balance: createQuantityAmount(),
});

const defaultItems = [createItem(), createItem()];

function SecurityInvestmentLedgerDetail({ formData }) {
  const { values = {}, onChangeValue } = formData || {};
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const today = new Date();

  // --- QUẢN LÝ STATE THÔNG TIN CHUNG ---
  const [headerInfo, setHeaderInfo] = useState({
    company: "",
    companyAddress: "",
    account: "",
    securityType: "",
    issuer: "",
    parValue: "",
    interestRate: "",
    paymentTerm: "",
    totalPages: "",
    openingDate: "",
    signingDay: today.getDate().toString(),
    signingMonth: (today.getMonth() + 1).toString(),
    signingYear: today.getFullYear().toString(),
    preparerName: "",
    chiefAccountantName: "",
    legalRepresentativeName: "",
  });

  // --- QUẢN LÝ STATE SỐ LIỆU ---
  const [openingBalance, setOpeningBalance] = useState(createQuantityAmount());
  const [localItems, setLocalItems] = useState(defaultItems);

  // Đồng bộ với formik values khi load API
  useEffect(() => {
    if (values.items && Array.isArray(values.items) && values.items.length > 0) {
      setLocalItems(values.items);
    }
    if (values.openingBalance) {
      setOpeningBalance({ ...createQuantityAmount(), ...values.openingBalance });
    }
    setHeaderInfo((prev) => ({ ...prev, ...values }));
  }, [values]);

  const handleHeaderChange = (field, val) => {
    setHeaderInfo((prev) => ({ ...prev, [field]: val }));
    if (onChangeValue) onChangeValue(field, val);
  };

  const num = (val) => (val && val !== "" && !isNaN(val) ? parseFloat(val) : 0);

  // Logic tính toán lại toàn bộ "Số dư" (Số lượng & Thành tiền) cho từng dòng
  const recalculateBalances = (items, openBal) => {
    let currentBal = {
      quantity: num(openBal.quantity),
      amount: num(openBal.amount),
    };

    return items.map((item) => {
      currentBal = {
        quantity: currentBal.quantity + num(item.bought.quantity) - num(item.sold.quantity),
        amount: currentBal.amount + num(item.bought.amount) - num(item.sold.amount),
      };
      return { ...item, balance: { ...currentBal } };
    });
  };

  const handleOpeningBalanceChange = (field, val) => {
    const newOpenBal = { ...openingBalance, [field]: val };
    setOpeningBalance(newOpenBal);
    setLocalItems(recalculateBalances(localItems, newOpenBal));
  };

  const handleChangeItem = (index, mainField, subField, value) => {
    let newItems = [...localItems];
    if (subField) {
      newItems[index] = {
        ...newItems[index],
        [mainField]: { ...newItems[index][mainField], [subField]: value },
      };
    } else {
      newItems[index] = { ...newItems[index], [mainField]: value };
    }

    // Nếu thay đổi Mua vào / Xuất ra -> Tính lại số dư
    if (mainField === "bought" || mainField === "sold") {
      newItems = recalculateBalances(newItems, openingBalance);
    }
    setLocalItems(newItems);
  };

  const addRow = () => setLocalItems([...localItems, createItem()]);
  const removeRow = (index) => {
    if (localItems.length <= 1) return;
    const newItems = localItems.filter((_, i) => i !== index);
    setLocalItems(recalculateBalances(newItems, openingBalance));
  };

  // --- LOGIC TÍNH TỔNG CỘNG ---
  const calculatedTotals = useMemo(() => {
    const sum = (type, field) => localItems.reduce((acc, item) => acc + num(item[type][field]), 0);
    return {
      totalArisingBought: {
        quantity: sum("bought", "quantity"),
        amount: sum("bought", "amount"),
      },
      totalArisingSold: {
        quantity: sum("sold", "quantity"),
        amount: sum("sold", "amount"),
      },
    };
  }, [localItems]);

  const closingBalance = useMemo(() => {
    if (localItems.length > 0) return localItems[localItems.length - 1].balance;
    return {
      quantity: num(openingBalance.quantity),
      amount: num(openingBalance.amount),
    };
  }, [localItems, openingBalance]);

  useEffect(() => {
    if (onChangeValue) {
      onChangeValue("items", localItems);
      onChangeValue("openingBalance", openingBalance);
      onChangeValue("totalArisingBought", calculatedTotals.totalArisingBought);
      onChangeValue("totalArisingSold", calculatedTotals.totalArisingSold);
      onChangeValue("closingBalance", closingBalance);
    }
  }, [localItems, openingBalance, calculatedTotals, closingBalance]);

  // --- STYLES ---
  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "13px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "15px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "13px",
      fontFamily: "inherit",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "50px",
      fontSize: "13px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    titleBox: { textAlign: "center", margin: "10px 0 20px 0" },
    titleMain: { fontSize: "16px", fontWeight: "bold", margin: 0, textTransform: "uppercase" },
    projectInfo: { width: "80%", margin: "0 auto", lineHeight: "1.8", textAlign: "center" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "12px", marginTop: "15px" },
    th: {
      border: "1px solid #000",
      padding: "6px 4px",
      textAlign: "center",
      fontWeight: "bold",
      verticalAlign: "middle",
    },
    td: { border: "1px solid #000", padding: 0, height: "35px", verticalAlign: "middle" },
    tdText: { border: "1px solid #000", padding: "0 6px", height: "35px", verticalAlign: "middle" },
    inputTable: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: "12px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputTableCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: "12px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: "12px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    totalText: {
      padding: "0 6px",
      textAlign: "right",
      fontWeight: "bold",
      display: "block",
      lineHeight: "35px",
    },
    rowBg: { backgroundColor: "#fafafa", fontWeight: "bold" },
    removeBtn: {
      border: "none",
      background: "transparent",
      color: "red",
      fontWeight: "bold",
      cursor: "pointer",
      width: "100%",
      height: "100%",
    },
  };

  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "40%" }}>
          <div style={{ fontWeight: "bold", marginBottom: 5 }}>
            Đơn vị:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "250px", fontWeight: "bold" }}
              value={headerInfo.company}
              onChange={(e) => handleHeaderChange("company", e.target.value)}
            />
          </div>
          <div style={{ fontWeight: "bold" }}>
            Địa chỉ:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "200px", fontWeight: "bold" }}
              value={headerInfo.companyAddress}
              onChange={(e) => handleHeaderChange("companyAddress", e.target.value)}
            />
          </div>
        </div>
        <div style={{ textAlign: "center", width: "60%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số S45-DN</div>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginTop: 4 }}>
            (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ
            Tài chính)
          </div>
        </div>
      </div>

      {/* TITLE */}
      <div style={s.titleBox}>
        <h2 style={s.titleMain}>SỔ CHI TIẾT ĐẦU TƯ CHỨNG KHOÁN</h2>
        <div style={{ fontWeight: "bold", marginTop: 5 }}>(Dùng cho các TK: 121, 228)</div>
      </div>

      <div style={s.projectInfo}>
        <div style={{ fontWeight: "bold" }}>
          Tài khoản:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "250px", textAlign: "center" }}
            value={headerInfo.account}
            onChange={(e) => handleHeaderChange("account", e.target.value)}
          />
        </div>
        <div>
          <span style={{ fontWeight: "bold" }}>Loại chứng khoán:</span>{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px" }}
            value={headerInfo.securityType}
            onChange={(e) => handleHeaderChange("securityType", e.target.value)}
          />
          <span style={{ fontWeight: "bold", marginLeft: "15px" }}>Đơn vị phát hành:</span>{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "200px" }}
            value={headerInfo.issuer}
            onChange={(e) => handleHeaderChange("issuer", e.target.value)}
          />
        </div>
        <div>
          <span style={{ fontWeight: "bold" }}>Mệnh giá:</span>{" "}
          <input
            disabled={containView}
            type="number"
            style={{ ...s.inputDotted, width: "100px" }}
            value={headerInfo.parValue}
            onChange={(e) => handleHeaderChange("parValue", e.target.value)}
          />
          <span style={{ fontWeight: "bold", marginLeft: "15px" }}>Lãi suất:</span>{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "80px" }}
            value={headerInfo.interestRate}
            onChange={(e) => handleHeaderChange("interestRate", e.target.value)}
          />
          <span style={{ fontWeight: "bold", marginLeft: "15px" }}>Thời hạn thanh toán:</span>{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px" }}
            value={headerInfo.paymentTerm}
            onChange={(e) => handleHeaderChange("paymentTerm", e.target.value)}
          />
        </div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th rowSpan={3} style={{ ...s.th, width: "65px" }}>
                Ngày
                <br />
                tháng
                <br />
                ghi sổ
              </th>
              <th colSpan={2} style={s.th}>
                Chứng từ
              </th>
              <th rowSpan={3} style={{ ...s.th, width: "180px" }}>
                Diễn giải
              </th>
              <th rowSpan={3} style={{ ...s.th, width: "45px" }}>
                TK
                <br />
                đối
                <br />
                ứng
              </th>
              <th colSpan={4} style={s.th}>
                Số phát sinh
              </th>
              <th colSpan={2} style={s.th}>
                Số dư
              </th>
              {!containView && (
                <th rowSpan={3} style={{ ...s.th, width: "35px" }}>
                  #
                </th>
              )}
            </tr>
            <tr>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>
                Số
                <br />
                hiệu
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>
                Ngày
                <br />
                tháng
              </th>
              <th colSpan={2} style={s.th}>
                Mua vào
              </th>
              <th colSpan={2} style={s.th}>
                Xuất ra
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "80px" }}>
                Số
                <br />
                lượng
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "110px" }}>
                Thành
                <br />
                tiền
              </th>
            </tr>
            <tr>
              <th style={{ ...s.th, width: "80px" }}>
                Số
                <br />
                lượng
              </th>
              <th style={{ ...s.th, width: "110px" }}>
                Thành
                <br />
                tiền
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Số
                <br />
                lượng
              </th>
              <th style={{ ...s.th, width: "110px" }}>
                Thành
                <br />
                tiền
              </th>
            </tr>
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>C</th>
              <th style={s.th}>D</th>
              <th style={s.th}>E</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              <th style={s.th}>3</th>
              <th style={s.th}>4</th>
              <th style={s.th}>5</th>
              <th style={s.th}>6</th>
              {!containView && <th style={s.th}></th>}
            </tr>
          </thead>
          <tbody>
            {/* 1. SỐ DƯ ĐẦU KỲ */}
            <tr style={s.rowBg}>
              <td style={s.td} colSpan={3}></td>
              <td style={s.tdText}>- Số dư đầu kỳ</td>
              <td style={s.td} colSpan={5}></td>
              <td style={s.td}>
                <input
                  disabled={containView}
                  type="number"
                  style={{ ...s.inputTable, fontWeight: "bold" }}
                  value={openingBalance.quantity}
                  onChange={(e) => handleOpeningBalanceChange("quantity", e.target.value)}
                />
              </td>
              <td style={s.td}>
                <input
                  disabled={containView}
                  type="number"
                  style={{ ...s.inputTable, fontWeight: "bold" }}
                  value={openingBalance.amount}
                  onChange={(e) => handleOpeningBalanceChange("amount", e.target.value)}
                />
              </td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* LABEL PHÁT SINH */}
            <tr>
              <td style={s.td} colSpan={3}></td>
              <td style={s.tdText} className="bold">
                - Số phát sinh trong kỳ:
              </td>
              <td colSpan={7} style={s.td}></td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* DÒNG PHÁT SINH (ITEMS) */}
            {localItems.map((row, i) => (
              <tr key={i}>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.recordingDate || ""}
                    onChange={(e) => handleChangeItem(i, "recordingDate", null, e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.documentNumber || ""}
                    onChange={(e) => handleChangeItem(i, "documentNumber", null, e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.documentDate || ""}
                    onChange={(e) => handleChangeItem(i, "documentDate", null, e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableLeft}
                    value={row.description || ""}
                    onChange={(e) => handleChangeItem(i, "description", null, e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.correspondingAccount || ""}
                    onChange={(e) =>
                      handleChangeItem(i, "correspondingAccount", null, e.target.value)
                    }
                  />
                </td>

                {/* Mua vào */}
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.bought.quantity || ""}
                    onChange={(e) => handleChangeItem(i, "bought", "quantity", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.bought.amount || ""}
                    onChange={(e) => handleChangeItem(i, "bought", "amount", e.target.value)}
                  />
                </td>

                {/* Xuất ra */}
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.sold.quantity || ""}
                    onChange={(e) => handleChangeItem(i, "sold", "quantity", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.sold.amount || ""}
                    onChange={(e) => handleChangeItem(i, "sold", "amount", e.target.value)}
                  />
                </td>

                {/* Số dư (Auto Tính) */}
                <td style={s.td}>
                  <input
                    disabled={true}
                    type="number"
                    style={s.inputTable}
                    value={row.balance.quantity || ""}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={true}
                    type="number"
                    style={s.inputTable}
                    value={row.balance.amount || ""}
                  />
                </td>

                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeRow(i)}>
                      X
                    </button>
                  </td>
                )}
              </tr>
            ))}

            {/* CỘNG SỐ PHÁT SINH */}
            <tr style={s.rowBg}>
              <td style={s.td} colSpan={3}></td>
              <td style={s.tdText}>- Cộng số phát sinh</td>
              <td style={s.tdText} className="text-center">
                x
              </td>

              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotals.totalArisingBought.quantity)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotals.totalArisingBought.amount)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotals.totalArisingSold.quantity)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotals.totalArisingSold.amount)}</span>
              </td>

              <td style={s.tdText} className="text-center">
                x
              </td>
              <td style={s.tdText} className="text-center">
                x
              </td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* SỐ DƯ CUỐI KỲ */}
            <tr style={s.rowBg}>
              <td style={s.td} colSpan={3}></td>
              <td style={s.tdText}>- Số dư cuối kỳ</td>
              <td style={s.tdText} className="text-center">
                x
              </td>
              <td style={s.tdText} className="text-center">
                x
              </td>
              <td style={s.tdText} className="text-center">
                x
              </td>
              <td style={s.tdText} className="text-center">
                x
              </td>
              <td style={s.tdText} className="text-center">
                x
              </td>

              <td style={s.td}>
                <span style={s.totalText}>{fmt(closingBalance.quantity)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(closingBalance.amount)}</span>
              </td>
              {!containView && <td style={s.td}></td>}
            </tr>
          </tbody>
        </table>
        {!containView && (
          <SoftBox mt={3} textAlign="center">
            <SoftButton variant="outlined" color="dark" onClick={addRow}>
              Thêm dòng
            </SoftButton>
          </SoftBox>
        )}
      </div>

      {/* FOOTER INFO */}
      <div style={{ marginTop: "20px", lineHeight: "1.8" }}>
        <div>
          - Sổ này có{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "40px" }}
            value={headerInfo.totalPages}
            onChange={(e) => handleHeaderChange("totalPages", e.target.value)}
          />{" "}
          trang, đánh số từ trang 01 đến trang...
        </div>
        <div>
          - Ngày mở sổ:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px" }}
            value={headerInfo.openingDate}
            onChange={(e) => handleHeaderChange("openingDate", e.target.value)}
          />
        </div>
      </div>

      {/* FOOTER CHỮ KÝ */}
      {/* <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "30px",
          textAlign: "center",
        }}
      >
        <div style={{ width: "33%" }}>
          <b>Người ghi sổ</b>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "65px" }}>
            (Ký, họ tên)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.preparerName}
            onChange={(e) => handleHeaderChange("preparerName", e.target.value)}
          />
        </div>
        <div style={{ width: "33%" }}>
          <b>Kế toán trưởng</b>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "65px" }}>
            (Ký, họ tên)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.chiefAccountantName}
            onChange={(e) => handleHeaderChange("chiefAccountantName", e.target.value)}
          />
        </div>
        <div style={{ width: "33%" }}>
          <div style={{ fontStyle: "italic", marginBottom: 5 }}>
            Ngày
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, marginLeft: 4 }}
              value={headerInfo.signingDay}
              onChange={(e) => handleHeaderChange("signingDay", e.target.value)}
            />
            tháng
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, marginLeft: 4 }}
              value={headerInfo.signingMonth}
              onChange={(e) => handleHeaderChange("signingMonth", e.target.value)}
            />
            năm
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, width: "40px", marginLeft: 4 }}
              value={headerInfo.signingYear}
              onChange={(e) => handleHeaderChange("signingYear", e.target.value)}
            />
          </div>
          <b>Người đại diện theo pháp luật</b>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "45px" }}>
            (Ký, họ tên, đóng dấu)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.legalRepresentativeName}
            onChange={(e) => handleHeaderChange("legalRepresentativeName", e.target.value)}
          />
        </div>
      </div> */}
    </SoftBox>
  );
}

SecurityInvestmentLedgerDetail.propTypes = {
  formData: PropTypes.object,
};

export default SecurityInvestmentLedgerDetail;
